<template>
    <div class="ActionsButton">
        <FontAwesomeIcon
            v-if="loading"
            :icon="faSpinner"
            class="ActionsButton__loadingSpinner"
            spin
        />
        <Expandable
            v-else-if="shouldShowActionsMenu"
            :menu-config="{ positionX: 'end', teleport: true, portalTarget: 'default-popover-target', autoFlip: 'y' }"
            @open="$emit('actionsButtonClicked')"
        >
            <template #trigger="{ toggle }">
                <NewButton
                    class="ActionsButton ActionsButton__clickable"
                    variant="flat"
                    size="icon-sm"
                    color="secondary"
                    @click="toggle"
                >
                    <EidIcon :icon-src="faEllipsisVertical" />
                </NewButton>
            </template>
            <template #default="{ close }">
                <div>
                    <template v-if="entity.active">
                        <ActionButtonItemWrapper :config="reactiveMenuConfig.pauseAndUnpause" @item-clicked="close()" />
                        <ActionButtonItemWrapper :config="reactiveMenuConfig.activation" @item-clicked="close()" />
                        <ActionButtonItemWrapper
                            v-if="showRemoveExceptionButton"
                            :config="staticMenuConfig.removeException"
                            @item-clicked="close()"
                        />
                        <ActionButtonItemWrapper
                            v-else-if="showGrantExceptionButton"
                            :config="staticMenuConfig.grantException"
                        >
                            <div class="flex flex-col">
                                <div class="flex gap-2 items-center">
                                    <FontAwesomeIcon class="w-4 h-4" :icon="faSlidersH" />
                                    Grant exception for entire entity
                                </div>
                                <div class="text-xs text-eidLightGray text-left">
                                    All non-compliant status will be waived as compliant. This can be undone.
                                </div>
                            </div>
                        </ActionButtonItemWrapper>
                        <ActionButtonItemWrapper :config="staticMenuConfig.createNewLink" @item-clicked="close()" />
                        <ActionButtonItemWrapper
                            :config="reactiveMenuConfig.copySubmissionLink"
                            @item-clicked="close()"
                        />
                        <ActionButtonItemWrapper
                            v-if="showViewActionReviewButton"
                            :config="staticMenuConfig.viewActionReview"
                            @item-clicked="close()"
                        />
                        <ActionButtonItemWrapper
                            v-if="showViewMappingButton"
                            :config="staticMenuConfig.viewMapping"
                            @item-clicked="close()"
                        />
                    </template>
                    <ActionButtonItemWrapper v-else :config="reactiveMenuConfig.activation" @item-clicked="close()" />
                    <ActionButtonItemWrapper :config="staticMenuConfig.viewSubmissionHistory" @item-clicked="close()" />
                    <ActionButtonItemWrapper :config="staticMenuConfig.uploadDocument" @item-clicked="close()" />
                    <ActionButtonItemWrapper :config="staticMenuConfig.addNote" @item-clicked="close()" />
                </div>
            </template>
        </Expandable>
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import noop from 'lodash/noop';

    import {
        faSquareCheck, faLink, faPauseCircle, faStopCircle, faPlayCircle, faClockRotateLeft,
        faSlidersH, faSpinner, faPaperPlane, faArrowUpFromBracket, faNoteSticky,
    } from '@fortawesome/pro-light-svg-icons';
    import {
        faEllipsisVertical,
    } from '@fortawesome/pro-regular-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { faShareNodes } from '@evidentid/dashboard-commons/assets/custom-icon-definitions';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { useUserStore } from '@evidentid/dashboard-commons/modules/auth/store/user';
    import RbacDisplayGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import {
        hasSufficientFeatures,
    } from '@evidentid/dashboard-commons/modules/role-base-access-control/utils/has-sufficient-features/hasSuffiencientFeatures';
    import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard';
    import {
        ComplianceStatus,
        ComplianceStatusOverride,
        VerificationStatus,
    } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import Expandable from '@/components/expandable/Expandable.vue';
    import NewButton from '@/components/new-button/NewButton.vue';
    import {
        INACTIVE_ENTITY_MENU_REQUIRED_ONE_OF_FEATURES,
    } from '@/modules/dashboard/constants/inactiveEntityMenuRequiredOneOfFeatures.constant';
    import { isEligibleForNewLink } from '@/utils/is-eligible-for-new-link/isEligibleForNewLink';

    import ActionButtonItemWrapper from './sub-components/ActionButtonItemWrapper.vue';

    @Component({
        emits: [ 'actionsButtonClicked', 'add-note', 'copySubmissionLink', 'grantExceptionToEntity', 'open-collateral-mapping-modal', 'removeExceptions', 'sendDocumentRequest', 'setEntitiesActiveState', 'setEntitiesPauseState', 'upload-entity-document', 'view-submission-history' ],
        components: {
            RbacDisplayGuard, FontAwesomeIcon, Expandable, ActionButtonItemWrapper, EidIcon, NewButton,
        },
    })
    export default class ActionsButton extends Vue {
        @Prop({ type: Object })
        private entity!: Entity;

        @Prop({ type: Object })
        private exceptionOverride!: ComplianceStatusOverride;

        @Prop({ type: Boolean, default: false })
        private loading!: boolean;

        @Prop({ type: Boolean, default: false })
        private isLoadingSubmissionLink!: boolean;

        @Prop({ type: String, default: '' })
        private submissionLink!: string;

        // Temporary to not allow them in certain place until PM decision for later release. subject to be removed
        @Prop({ type: Boolean, default: false })
        private hideExceptionOptions!: boolean;

        @Prop({ type: Boolean, default: false })
        private hideViewMapping!: boolean;

        @Prop({ type: Boolean, default: false })
        private isCollateralEnabled!: boolean;

        private RbacFeature = RbacFeature;
        private faEllipsisVertical = faEllipsisVertical;
        private faPaperPlane = faPaperPlane;
        private faPauseCircle = faPauseCircle;
        private faPlayCircle = faPlayCircle;
        private faShareNodes = faShareNodes;
        private faSlidersH = faSlidersH;
        private faSpinner = faSpinner;
        private faStopCircle = faStopCircle;
        private faClockRotateLeft = faClockRotateLeft;

        private staticMenuConfig = {
            removeException: {
                icon: faSlidersH,
                label: 'Remove exception',
                requiredFeatures: [ RbacFeature.entityDetailsAddRemoveException ],
                action: this.removeExceptionFromEntity,
            },
            grantException: {
                icon: faSlidersH,
                label: 'Grant exception',
                requiredFeatures: [ RbacFeature.entityDetailsAddRemoveException ],
                action: this.grantExceptionToEntity,
            },
            createNewLink: {
                icon: faPaperPlane,
                label: 'Create a New Link',
                disabled: !this.shouldEnableDocumentRequests,
                requiredFeatures: [ RbacFeature.dashboardCreateNewLink ],
                action: this.sendDocumentRequest,
            },
            viewActionReview: {
                icon: faSquareCheck,
                label: 'View pending actions',
                requiredFeatures: [ RbacFeature.dashboardViewPendingActions ],
                action: this.navigateToActionReview,
            },
            viewMapping: {
                icon: faShareNodes,
                label: 'View mapping',
                requiredFeatures: [],
                action: this.openMappingAdjustmentModal,
            },
            viewSubmissionHistory: {
                icon: faClockRotateLeft,
                label: 'View submission history',
                requiredFeatures: [ RbacFeature.dashboardViewSubmissionHistory ],
                action: this.openHistoricDocumentModal,
            },
            uploadDocument: {
                icon: faArrowUpFromBracket,
                label: 'Add a File for Reference',
                requiredFeatures: [ RbacFeature.dashboardUploadEntityDocument ],
                action: this.uploadEntityDocument,
            },
            addNote: {
                icon: faNoteSticky,
                label: 'Add Note',
                requiredFeatures: [ RbacFeature.entityNoteCreate ],
                action: this.addNote,
            },
        };

        private get reactiveMenuConfig(): Record<string, any> {
            return {
                // Lives here rather than in staticMenuConfig because that is a plain field initializer,
                // evaluated once at construction -- a toggle there would go stale when `entity.active`
                // changes without the component being re-created.
                activation: {
                    icon: this.entity.active ? faStopCircle : faPlayCircle,
                    label: this.entity.active ? 'Deactivate' : 'Activate',
                    requiredFeatures: [ RbacFeature.dashboardActivateDeactivateEntity ],
                    action: this.setEntityActiveState,
                },
                // Same reasoning as activation above: `setEntitiesPauseState` doesn't reload the
                // list, and rows are keyed by id, so this component instance survives a pause
                // toggle and has to follow `entity.paused` rather than the value it was built with.
                pauseAndUnpause: {
                    icon: this.entity.paused ? faPlayCircle : faPauseCircle,
                    label: this.entity.paused ? 'Unpause' : 'Pause',
                    requiredFeatures: [ RbacFeature.dashboardPauseEntity ],
                    action: this.changePauseState,
                },
                copySubmissionLink: {
                    icon: this.isLoadingSubmissionLink ? faSpinner : faLink,
                    iconProps: this.isLoadingSubmissionLink ? { spin: true } : {},
                    label: 'Copy submission link to clipboard',
                    disabled: this.isLoadingSubmissionLink || !this.hasSubmissionLink,
                    requiredFeatures: [ RbacFeature.dashboardCopySubmissionLink ],
                    action: this.copySubmissionLink,
                },
            };
        }

        private get shouldEnableDocumentRequests(): boolean {
            return isEligibleForNewLink(this.entity);
        }

        private get shouldShowActionsMenu(): boolean {
            return this.entity.active || hasSufficientFeatures(
                INACTIVE_ENTITY_MENU_REQUIRED_ONE_OF_FEATURES,
                useUserStore().currentRpRbacFeatures || {},
                true,
            );
        }

        private get isNonCompliant(): boolean {
            return this.entity.complianceStatus === ComplianceStatus.nonCompliant;
        }

        private get hasSubmissionLink(): boolean {
            return Boolean(this.submissionLink);
        }

        private get showViewActionReviewButton(): boolean {
            return this.entity.verificationStatus === VerificationStatus.actionsReview && this.entity.active;
        }

        private get showViewMappingButton(): boolean {
            return this.isCollateralEnabled && !this.hideViewMapping;
        }

        private get showRemoveExceptionButton(): boolean {
            return Boolean(!this.hideExceptionOptions && this.exceptionOverride);
        }

        private get showGrantExceptionButton(): boolean {
            return !this.hideExceptionOptions && this.isNonCompliant;
        }

        private changePauseState() {
            this.$emit('setEntitiesPauseState', {
                entities: [ this.entity ],
                value: !this.entity.paused,
                isBulk: false,
            });
        }

        private setEntityActiveState() {
            this.$emit('setEntitiesActiveState', {
                entities: [ this.entity ],
                value: !this.entity.active,
                isBulk: false,
            });
        }

        private sendDocumentRequest() {
            this.$emit('sendDocumentRequest', [ this.entity ]);
        }

        private copySubmissionLink(): void {
            this.$emit('copySubmissionLink', this.submissionLink);
        }

        private grantExceptionToEntity(): void {
            this.$emit('grantExceptionToEntity');
        }

        private removeExceptionFromEntity(): void {
            this.$emit('removeExceptions', [ this.exceptionOverride.id ]);
        }

        private navigateToActionReview(): void {
            this.$router.push({
                name: 'entityActionsReview',
                query: { entityId: this.entity.id },
            } as any).catch(noop);
        }

        private openMappingAdjustmentModal(): void {
            this.$emit('open-collateral-mapping-modal');
        }

        private openHistoricDocumentModal(): void {
            this.$emit('view-submission-history');
        }

        private uploadEntityDocument(): void {
            this.$emit('upload-entity-document');
        }

        private addNote(): void {
            this.$emit('add-note', this.entity);
        }
    }
</script>
